import { Link } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import { reminderNoteLabel } from '@/db/domain/customerReminders'
import { toZaDateKey, toZaTime } from '@/lib/datetime'
import { useDb } from '@/lib/dbContext'
import { WhatsAppMessagesTab } from '@/screens/WhatsAppMessagesTab'

const zaLabel = (iso?: string) =>
  iso ? `${toZaDateKey(iso)} ${toZaTime(iso)}` : ''

/** One reminder: who it is for, where it stands, and every message it queued. */
export function ReminderDetail({ reminderId }: { reminderId: string }) {
  const db = useDb()
  const remindersResult = useLiveQuery((q) =>
    q.from({ reminders: db.customerRemindersCollection }),
  )
  const customersResult = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )

  useEffect(() => {
    void db.customerRemindersCollection.preload?.()
  }, [db])

  const reminder = (remindersResult.data ?? []).find((r) => r.id === reminderId)
  const customer = (customersResult.data ?? []).find(
    (c) => c.id === reminder?.customerId,
  )

  if (remindersResult.isLoading) {
    return <p className="text-sm text-slate-600">Loading reminder…</p>
  }
  if (!reminder) {
    return (
      <p className="text-sm text-slate-600">
        This reminder doesn't exist.{' '}
        <Link to="/reminders" className="font-medium text-blue-700 hover:underline">
          Back to scheduled reminders
        </Link>
      </p>
    )
  }

  const note = reminderNoteLabel(reminder.statusNote)
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Link
          to={reminder.state === 'archived' ? '/reminders/archived' : '/reminders'}
          className="text-[13px] text-blue-700 hover:underline"
        >
          ← {reminder.state === 'archived' ? 'Archived' : 'Scheduled'}
        </Link>
        <h2 className="text-lg font-semibold">
          Reminder for{' '}
          {customer ? (
            <Link
              to="/customers/$id"
              params={{ id: customer.publicId ?? customer.id }}
              className="text-blue-700 hover:underline"
            >
              {customer.fullName}
            </Link>
          ) : (
            'unknown customer'
          )}
        </h2>
        <p className="text-[13px] text-slate-500">
          {reminder.state === 'archived'
            ? `Archived ${zaLabel(reminder.archivedAt)} — everything was collected.`
            : reminder.state === 'stopped'
              ? 'Stopped by staff.'
              : reminder.state === 'expired'
                ? 'Expired — past the maximum age.'
                : reminder.nextSendAt
                  ? `Active — next message ${zaLabel(reminder.nextSendAt)}.`
                  : 'Active.'}{' '}
          Started {zaLabel(reminder.startedAt)}.{note ? ` ${note}.` : ''}
        </p>
      </div>
      <WhatsAppMessagesTab reminderId={reminder.id} />
    </div>
  )
}
